import React, { useEffect } from "react";
import { Trophy, Check } from "lucide-react";
import styled, { css } from "styled-components";

import theme from "../../styles/theme";
import { useInventoryContext } from "../../contexts/inventory";

/* Дані чемпіонату. Коли з'явиться API — міняється лише це джерело. */
const CHAMPIONSHIP = {
  name: "Formula 4 Series 2026",
  rounds: 7,
  raceDay: "щопонеділка",
  seasonStartsAt: "2026-10-19",
  seasonEndsAt: "2026-12-08",
  slotsTotal: 40,
  slotsTaken: 32,
  registrationOpensAt: "2026-09-15",
  registrationClosesAt: null,
  registerUrl: "/register",
};

/* Нижче цієї частки вільних місць прогрес-бар стає помаранчевим */
const LOW_SLOTS_RATIO = 0.5;
const AMBER = "#ff3900";

const formatDate = (value) =>
  new Date(value).toLocaleDateString("uk-UA", {
    day: "2-digit",
    month: "2-digit",
  });

const KYIV_TIME_ZONE = "Europe/Kyiv";

/* "YYYY-MM-DD" мітить північ за київським часом, а не UTC (важливо через різницю в 2-3 год) */
const getTimeZoneOffsetMinutes = (timeZone, date) => {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  })
    .formatToParts(date)
    .reduce((acc, part) => {
      acc[part.type] = part.value;
      return acc;
    }, {});

  const asUTC = Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    Number(parts.hour),
    Number(parts.minute),
    Number(parts.second),
  );

  return (asUTC - date.getTime()) / 60000;
};

const kyivDateStringToTimestamp = (value) => {
  const utcGuess = new Date(`${value}T00:00:00Z`);
  const offsetMinutes = getTimeZoneOffsetMinutes(KYIV_TIME_ZONE, utcGuess);
  return utcGuess.getTime() - offsetMinutes * 60000;
};

const getRegistrationState = ({ championship, registered }) => {
  const { registrationOpensAt, registrationClosesAt, slotsTaken, slotsTotal } = championship;
  const now = Date.now();
  
  if (registrationOpensAt && now < kyivDateStringToTimestamp(registrationOpensAt)) {
    return "upcoming";
  }
  if (registered) return "registered";
  if (
    slotsTaken >= slotsTotal ||
    (registrationClosesAt && now > kyivDateStringToTimestamp(registrationClosesAt))
  ) {
    return "closed";
  }
  return "open";
};

const Widget = styled.div`
  position: relative;
  border: 2px solid #67676d4d;
  border-radius: 4px;
  overflow: hidden;
  background-color: #000;
  font-family: "Montserrat", sans-serif;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);

  &::before {
    content: "";
    position: absolute;
    top: -60px;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: url("https://cdn.slickracing.com.ua/images/reg_widget.jpg");
    background-size: cover;
    background-position: center;
    opacity: 40%;
  }

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(
      100deg,
      rgba(0, 0, 0, 0.8) 0%,
      rgba(0, 0, 0, 0.6) 45%,
      rgba(0, 0, 0, 0.4) 100%
    );
  }

  & > * {
    position: relative;
    z-index: 1;
  }
`;

const Strip = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px 24px;
  flex-wrap: wrap;
  padding: 20px 24px;

  @media screen and (max-width: 720px) {
    padding: 12px 14px;
    gap: 12px;
  }
`;

const Left = styled.div`
  display: flex;
  align-items: center;
  gap: 11px;
  min-width: 0;

  & > svg {
    flex-shrink: 0;
  }

  @media screen and (max-width: 720px) {
    width: 100%;
  }
`;

const Titles = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
`;

const Name = styled.span`
  font-size: 17px;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: ${theme.colors.white};

  @media screen and (max-width: 720px) {
    font-size: 15px;
  }
`;

const Meta = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: #b7b7be;
`;

const Separator = styled.span`
  color: #6d6d76;
  margin: 0 6px;
`;

const Right = styled.div`
  display: flex;
  align-items: center;
  gap: 18px;
  flex-wrap: wrap;

  @media screen and (max-width: 720px) {
    width: 100%;
  }
`;

const Slots = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 150px;

  @media screen and (max-width: 720px) {
    flex: 1;
    min-width: 120px;
  }
`;

const Count = styled.span`
  display: flex;
  align-items: baseline;
  gap: 5px;
  font-variant-numeric: tabular-nums;

  b {
    font-size: 22px;
    font-weight: 600;
    line-height: 1;
    letter-spacing: -0.01em;
    color: ${theme.colors.white};
  }

  i {
    font-style: normal;
    font-size: 16px;
    font-weight: 500;
    color: ${theme.colors.grey};
  }

  small {
    font-size: 11.5px;
    color: ${theme.colors.grey};
    margin-left: 4px;
  }
`;

const Bar = styled.div`
  height: 6px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.3);
  overflow: hidden;

  span {
    display: block;
    height: 100%;
    width: ${({ $percent }) => $percent}%;
    border-radius: 999px;
    background: ${({ $full, $low }) => {
      if ($full) return "rgba(255, 255, 255, 0.28)";
      if ($low) return AMBER;
      return "#cfcfd4";
    }};
  }
`;

const ctaBase = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-width: 216px;
  padding: 10px 24px;
  border-radius: 4px;
  font-family: "Montserrat", sans-serif;
  font-size: 12.5px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  white-space: nowrap;
  text-decoration: none;

  @media screen and (max-width: 720px) {
    flex: 1;
    min-width: 0;
    padding: 10px 14px;
  }
`;

const RegisterLink = styled.a`
  ${ctaBase};
  color: ${theme.colors.green};
  background: rgba(184, 255, 46, 0.06);
  border: 2px solid ${theme.colors.green};
  cursor: pointer;
  transition:
    background 0.15s ease,
    transform 0.15s ease;

  &:hover {
    background: rgba(184, 255, 46, 0.14);
  }

  &:active {
    transform: translateY(1px);
  }

  &:focus-visible {
    outline: 2px solid ${theme.colors.green};
    outline-offset: 3px;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const RegisteredLabel = styled.span`
  ${ctaBase};
  color: ${theme.colors.green};
  background: rgba(184, 255, 46, 0.08);
  border: 2px solid rgba(184, 255, 46, 0.4);
  cursor: default;
`;

const InertLabel = styled.span`
  ${ctaBase};
  color: ${theme.colors.grey};
  background: rgba(255, 255, 255, 0.03);
  border: 2px solid rgba(255, 255, 255, 0.16);
  cursor: default;
`;

const ChampionshipWidget = ({ championship = CHAMPIONSHIP }) => {
  const [, setIsLoading] = React.useState(true);
  const { registered, loadProfileItems } = useInventoryContext();

  useEffect(() => {
    setIsLoading(true);
    loadProfileItems().finally(() => setIsLoading(false));
  }, [loadProfileItems]);

  const { name, rounds, raceDay, seasonStartsAt, seasonEndsAt, slotsTaken, slotsTotal } =
    championship;
  const state = getRegistrationState({ championship, registered });

  const percent = slotsTotal ? Math.min((slotsTaken / slotsTotal) * 100, 100) : 0;
  const isFull = slotsTaken >= slotsTotal;
  const isLow = !isFull && slotsTotal - slotsTaken <= slotsTotal * LOW_SLOTS_RATIO;

  const renderAction = () => {
    console.log("Registration state:", state);
    switch (state) {
      case "upcoming":
        return <InertLabel>Реєстрація з {formatDate(championship.registrationOpensAt)}</InertLabel>;
      case "registered":
        return (
          <RegisteredLabel>
            <Check size={15} strokeWidth={2.5} />
            Зареєстровано
          </RegisteredLabel>
        );
      case "closed":
        return <InertLabel>Реєстрацію закрито</InertLabel>;
      default:
        return <RegisterLink href={championship.registerUrl}>Зареєструватись</RegisterLink>;
    }
  };

  return (
    <div style={{ marginTop: 32, marginBottom: 32 }}>
      <Widget>
        <Strip>
          <Left>
            <Trophy color={state === "closed" ? theme.colors.grey : theme.colors.green} size={26} />
            <Titles>
              <Name>{name}</Name>
              <Meta>
                {formatDate(seasonStartsAt)} – {formatDate(seasonEndsAt)}
                <Separator>·</Separator>
                {rounds} етапів
                {/* <Separator>·</Separator>
                {raceDay} */}
              </Meta>
            </Titles>
          </Left>

          <Right>
            <Slots>
              <Count>
                <b>{slotsTaken}</b>
                <i>/ {slotsTotal}</i>
                <small>пілотів</small>
              </Count>
              <Bar $percent={percent} $low={isLow} $full={isFull}>
                <span />
              </Bar>
            </Slots>

            {renderAction()}
          </Right>
        </Strip>
      </Widget>
    </div>
  );
};

export default ChampionshipWidget;
